import React, { useMemo, useEffect } from 'react'
import { Flex, Box, Text } from 'rebass'
import { RouteComponentProps, useHistory } from 'react-router-dom'
import { useNewTranslation } from 'hooks/index'
import qs from 'query-string'
import {
    selectBanner,
    BannerState,
    DEEPLINK_TYPE,
    CreateBannerPayload,
    selectBannerGroups,
    getBannerGroups,
    getBanners,
    BannerGroupState,
    disableBanner,
    createBanner,
} from 'state/banners'
import { useDispatch, useSelector } from 'react-redux'
import { Formik, Form, FormikHelpers, useFormikContext, useField } from 'formik'
import { showSnackBar } from 'services/toast'
import OnFormikValidationError from 'components/OnFormikValidationError'
import * as yup from 'yup'
import Inputs from 'components/Inputs'
import { theme } from 'theme'
import { UploadType } from 'services/api/uploads'
import Divider from 'components/Divider'
import { selectDeepLinkTypeOptions, GLOBAL_MODAL_TYPE, openModal, closeModal } from 'state/constants'
import { parseBackendDate, convertToBackendStr } from 'services/dates'
import { MAX_PAGE_SIZE } from 'state/review'
import ConfirmModal from './ConfirmModal'
import { getBannerRedirectLinkOptionTranslation } from 'components/utils/translationsMap'
import { ERROR_CODES } from 'state/error'
import RectangleButton, { RectangleLinkButton } from 'particles/Button/RectangleButton'
import Dropdown from 'particles/Dropdown'
import MenuItem from 'particles/MenuItem'
import { FormikDatePicker } from 'particles/DatePicker'
import ImageUploadField from 'components/Shared/Fields/ImageUploadField'

enum EDIT_MODE {
    CREATE = 0,
    UPDATE,
}

interface EditorProps {
    bannerGroups?: BannerGroupState[]
    banner?: BannerState
    bannerGroupUuid?: string | null
    editMode: EDIT_MODE
}

interface SaveProps {
    bannerUuid?: string | null
    bannerGroupUuid?: string | null
    editMode: EDIT_MODE
    bannerPage: number
}

const Editor: React.FC<EditorProps> = ({ bannerGroups, banner, bannerGroupUuid, editMode }) => {
    const [newT] = useNewTranslation()
    const dispatch = useDispatch()
    const { setFieldValue, values, submitForm, validateForm } = useFormikContext<CreateBannerPayload>()
    const [deeplinkTypeField, deeplinkTypeMeta] = useField('deeplink_type')

    const deepLinkTypeOptions = useSelector(selectDeepLinkTypeOptions)

    const selectedDeeplinkType = deepLinkTypeOptions.find(option => deeplinkTypeField.value === option.id)
    const selectedBannerGroup =
        bannerGroups && bannerGroups.find(option => bannerGroupUuid && bannerGroupUuid === option.uuid)

    const onClickDisable = (e: any): void => {
        e.preventDefault()
        if (bannerGroupUuid && banner && banner.uuid) {
            dispatch(disableBanner({ bannerGroupUuid, bannerUuid: banner.uuid }))
            dispatch(closeModal())
        }
    }

    const onClickCreate = (e: any): void => {
        e.preventDefault()
        submitForm()
    }

    const onOpenDisableModal = (e: any): void => {
        e.preventDefault()
        dispatch(openModal(GLOBAL_MODAL_TYPE.BANNER_DISABLE))
    }

    const onOpenCreateModal = (e: any): void => {
        e.preventDefault()
        validateForm().then(errors => {
            const noError = Object.keys(errors).length === 0
            if (noError) {
                dispatch(openModal(GLOBAL_MODAL_TYPE.BANNER_CREATE))
            } else {
                submitForm()
            }
        })
    }

    const canDisableBanner = banner && banner.disabled_at == undefined
    return (
        <Box bg={theme.newColors.neutral50} flex={1} p={'24px'} sx={{ position: 'relative' }}>
            <Flex>
                <Box mb={'32px'} width={'368px'}>
                    <Inputs.TextField
                        label={newT('banner.bannerNameTableLabel', 'Banner name')}
                        name="name"
                        placeholder={newT('placeholderText.enterBannerName', 'Input banner name here')}
                        isDisabled={editMode === EDIT_MODE.UPDATE}
                    />
                </Box>
                <Box mx={'auto'} />
                {editMode === EDIT_MODE.UPDATE && (
                    <Box>
                        <RectangleButton
                            form="text"
                            size="m"
                            color="neutral"
                            label={newT('button.disableBanner', 'Disable Banner')}
                            onClick={onOpenDisableModal}
                            isDisabled={!canDisableBanner}
                        />
                    </Box>
                )}
            </Flex>

            <Box mb={'32px'} maxWidth={'342px'}>
                <ImageUploadField
                    name="image"
                    presetType={UploadType.BANNER}
                    title={newT('banner.imageTableLabel')}
                    subtitle={newT(
                        'bannerGroup.banner.input.image.description',
                        'To make sure your banner is shown on the app properly, please upload in 686x246 dimension',
                    )}
                    isReadOnly={editMode === EDIT_MODE.UPDATE}
                    requiredWidth={343}
                    requiredHeight={123}
                    isRequired={false}
                    maxFiles={1}
                    dataTestId="test-banner-upload"
                />
            </Box>

            <Box mb={'32px'} maxWidth={'368px'}>
                <Dropdown
                    label={newT('banner.selectBannerRedirectLink')}
                    isDisabled={editMode === EDIT_MODE.UPDATE}
                    placeholder={newT('placeholderText.clickToSelect')}
                    onChange={value => setFieldValue('deeplink_type' as never, value)}
                    errorMessage={deeplinkTypeMeta.touched ? deeplinkTypeMeta.error : undefined}
                    value={selectedDeeplinkType?.id}
                    isRequired
                    dataTestId="test-banner-redirect-link-type-selector"
                >
                    {deepLinkTypeOptions.map((option, index) => (
                        <MenuItem.Text
                            key={`search-dropdown-${index}`}
                            text={getBannerRedirectLinkOptionTranslation[option.label](newT)}
                            value={option.id}
                            tabIndex={index}
                        />
                    ))}
                </Dropdown>
                {values.deeplink_type === DEEPLINK_TYPE.CAMPAIGNS ||
                values.deeplink_type === DEEPLINK_TYPE.TASK_LIST ||
                values.deeplink_type === DEEPLINK_TYPE.EXTERNAL ? (
                    <Box mt={'12px'}>
                        <Inputs.TextField
                            label={newT('banner.enterRedirectLinkData', 'Redirect link data')}
                            name="deeplink_data"
                            placeholder={
                                values.deeplink_type === DEEPLINK_TYPE.CAMPAIGNS ||
                                values.deeplink_type === DEEPLINK_TYPE.TASK_LIST
                                    ? newT('placeholderText.enterCampaignId', 'Enter campaign ID')
                                    : newT(
                                          'bannerGroup.banner.input.deeplinkData.placeholder.externalLink',
                                          'Input external link here',
                                      )
                            }
                            isDisabled={editMode === EDIT_MODE.UPDATE}
                        />
                    </Box>
                ) : null}
            </Box>
            <Box mb={'32px'} maxWidth={'368px'}>
                <Dropdown
                    label={newT('banner.selectCountry')}
                    placeholder={newT('placeholderText.clickToSelect')}
                    value={selectedBannerGroup?.uuid}
                    isDisabled
                >
                    {bannerGroups?.map((option, index) => (
                        <MenuItem.Text
                            key={`search-dropdown-${index}`}
                            text={newT(`country.${option.country}`)}
                            value={option.uuid}
                            tabIndex={index}
                        />
                    ))}
                </Dropdown>
            </Box>
            <Box mb={'8px'} maxWidth={'370px'}>
                <FormikDatePicker
                    name={'time_window.start'}
                    size={'m'}
                    isDisabled={editMode === EDIT_MODE.UPDATE}
                    country={selectedBannerGroup?.country}
                    label={newT('general.information.startDate', 'Start Date')}
                    showTime={true}
                    width={'370px'}
                    placeholder="DD/MM/YYYY, hh:mm"
                />
            </Box>
            <Box mb={'32px'} maxWidth={'370px'}>
                <FormikDatePicker
                    name={'time_window.end'}
                    size={'m'}
                    isDisabled={editMode === EDIT_MODE.UPDATE}
                    country={selectedBannerGroup?.country}
                    label={newT('general.information.endDate', 'End Date')}
                    showTime={true}
                    width={'370px'}
                    placeholder="DD/MM/YYYY, hh:mm"
                />
            </Box>
            <Divider mb={'24px'} sx={{ borderWidth: '4px' }} />
            <Box sx={{ display: 'flex', justifyContent: 'right', gap: '24px' }}>
                <RectangleLinkButton
                    to={`/banners/bannerList?bannerGroup=${bannerGroupUuid}`}
                    form="outlined"
                    size="m"
                    label={newT('button.goBack', 'Go Back')}
                />
                <RectangleButton
                    data-testid="test-save-changes"
                    form="filled"
                    size="m"
                    label={newT('button.saveChanges', 'Save Changes')}
                    onClick={onOpenCreateModal}
                    isDisabled={editMode === EDIT_MODE.UPDATE}
                />
            </Box>
            <ConfirmModal
                title={newT(
                    'modalWindow.confirmationMessage.createBannerTitle',
                    'Please check that your banner details are correct!',
                )}
                description={newT(
                    'modal.banner.create.description',
                    'Once you click confirm, you’ll no longer be able to edit this banner in the future',
                )}
                onConfirm={onClickCreate}
                confirmText={newT('button.createBanner', 'Create Banner')}
                cancelText={newT('button.backToEdit', 'Back To Edit')}
                modalType={GLOBAL_MODAL_TYPE.BANNER_CREATE}
                size="s"
                isClosable={false}
            />
            <ConfirmModal
                title={newT(
                    'modalWindow.confirmationMessage.checkBannerDetailsTitle',
                    'Are you sure you want to disable this banner?',
                )}
                description={newT(
                    'modal.banner.disable.description',
                    'Once you disable this banner it will no longer be shown on the app',
                )}
                onConfirm={onClickDisable}
                confirmText={newT('button.confirmDisable', 'Yes, Disable')}
                cancelText={newT('button.cancel')}
                modalType={GLOBAL_MODAL_TYPE.BANNER_DISABLE}
                size="m"
                hasIcon={false}
            />
        </Box>
    )
}

const Save: React.FC<SaveProps> = ({ bannerUuid, bannerGroupUuid, editMode, bannerPage }) => {
    const [newT] = useNewTranslation()
    const dispatch = useDispatch()
    const { goBack } = useHistory()
    const banner = useSelector(selectBanner(bannerUuid))
    const bannerGroups = useSelector(selectBannerGroups)
    const selectedCountry = bannerGroups.find(option => bannerGroupUuid && bannerGroupUuid === option.uuid)?.country

    useEffect(() => {
        const offset: number = bannerPage * MAX_PAGE_SIZE
        if (bannerGroups.length == 0) {
            dispatch(getBannerGroups())
        }
        if (bannerGroupUuid) {
            dispatch(getBanners({ bannerGroupUuid, offset }))
        }
    }, [])

    const initialValue = useMemo(() => {
        const init = banner
            ? {
                  name: banner.name,
                  image: [banner.image],
                  time_window: {
                      start: selectedCountry
                          ? parseBackendDate(banner.time_window.start, selectedCountry, { showTime: true })
                          : null,
                      end: selectedCountry
                          ? parseBackendDate(banner.time_window.end, selectedCountry, { showTime: true })
                          : null,
                  },
                  deeplink_type: banner.deeplink_type,
                  deeplink_data: banner.deeplink_data,
              }
            : {
                  name: null,
                  image: null,
                  time_window: { start: null, end: null },
                  deeplink_type: null,
                  deeplink_data: null,
              }
        return init
    }, [banner])

    const validationSchema = yup.object({
        name: yup
            .string()
            .max(
                100,
                newT(
                    'errorMessage.validation.enterBannerTitleUnderCharacterLimit',
                    'Please limit your banner title to 100 characters',
                ),
            )
            .required(newT('errorMessage.validation.enterBannerName', 'Please input banner name'))
            .nullable(),
        image: yup
            .array()
            .required(newT('errorMessage.validation.uploadBanner', 'Please upload a banner image'))
            .nullable(),
        time_window: yup
            .object({
                start: yup
                    .string()
                    .required(
                        newT('errorMessage.validation.selectStartDateAndTime', 'Please enter a start date and time'),
                    )
                    .nullable(),
                end: yup
                    .string()
                    .required(newT('errorMessage.validation.selectEndDateAndTime', 'Please enter a end date and time'))
                    .nullable(),
            })
            .test({
                message: () => ({
                    start: newT(
                        'bannerGroup.banner.validation.dateRange',
                        'The end date cannot be before the Start Date',
                    ),
                    end: newT(
                        'bannerGroup.banner.validation.dateRange',
                        'The end date cannot be before the Start Date',
                    ),
                }),
                test: (value: { start?: string | null; end: string | null }) => {
                    const startDate = new Date(value && value.start ? value.start : '')
                    const endDate = new Date(value && value.end ? value.end : '')
                    return endDate >= startDate
                },
            }),
        deeplink_type: yup
            .number()
            .nullable()
            .required(newT('errorMessage.validation.selectRedirectLinkType', 'Please select redirect link type')),
        deeplink_data: yup
            .string()
            .nullable()
            .when('deeplink_type', {
                is: deeplink_type =>
                    deeplink_type === DEEPLINK_TYPE.CAMPAIGNS ||
                    deeplink_type === DEEPLINK_TYPE.TASK_LIST ||
                    deeplink_type === DEEPLINK_TYPE.EXTERNAL,
                then: yup
                    .string()
                    .required(newT('errorMessage.validation.enterRedirectData', 'Please enter redirect data')),
            })
            .when('deeplink_type', {
                is: deeplink_type => deeplink_type === DEEPLINK_TYPE.EXTERNAL,
                then: yup
                    .string()
                    .url(newT('errorMessage.validation.enterUrlLink'))
                    .nullable()
                    .max(
                        9999,
                        newT(
                            'errorMessage.validation.enterUrlCharacterLimit',
                            'Please enter URL under 9999 characters',
                        ),
                    ),
            }),
    })
    return (
        <Formik
            enableReinitialize={true}
            initialValues={initialValue}
            validationSchema={validationSchema}
            onSubmit={(values: any, actions: FormikHelpers<any>): void => {
                if (bannerGroupUuid && selectedCountry) {
                    dispatch(
                        createBanner({
                            name: values.name,
                            image: values.image[0],
                            time_window: {
                                start: convertToBackendStr(values.time_window.start, selectedCountry),
                                end: convertToBackendStr(values.time_window.end, selectedCountry),
                            },
                            deeplink_type: values.deeplink_type,
                            deeplink_data: values.deeplink_data,
                            bannerGroupUuid,
                            callback: error => {
                                // set form field error if there is error after submitting form
                                if (error) {
                                    if (error === ERROR_CODES.CAMPAIGN_NOT_FOUND) {
                                        const errMsg = newT('errorMessage.validation.enterValidCampaignId')
                                        actions.setFieldError('deeplink_data', errMsg)
                                    }
                                } else {
                                    // go back to banner list, when there is no error from submitting
                                    goBack()
                                }
                            },
                        }),
                    )
                    dispatch(closeModal())
                    actions.setSubmitting(false)
                }
            }}
        >
            {(): JSX.Element => (
                <Form>
                    <OnFormikValidationError
                        callback={(): void => {
                            showSnackBar({
                                title: newT(
                                    'bannerGroup.banner.validation.error.description',
                                    'Please fill in all the details required to save the banner. Double check the fields highlighted in red!',
                                ),
                                type: 'error',
                            })
                        }}
                    />
                    <Box flex={1} m={'24px'}>
                        <Box>
                            <Text
                                fontSize={'18px'}
                                fontWeight={'bold'}
                                color={theme.colors.mineShaft}
                                textAlign={'left'}
                            >
                                {editMode === EDIT_MODE.CREATE
                                    ? newT('banner.createBannerPageTitle', 'Create Banners')
                                    : newT('banner.bannerDetailsPageTitle', 'Banner Details')}
                            </Text>
                            <Divider mt={'24px'} mb={'32px'} sx={{ borderWidth: '4px' }} />
                        </Box>
                        <Editor
                            bannerGroups={bannerGroups}
                            banner={banner}
                            bannerGroupUuid={bannerGroupUuid}
                            editMode={editMode}
                        />
                    </Box>
                </Form>
            )}
        </Formik>
    )
}

const Wrapper: React.FC<RouteComponentProps> = ({ location }) => {
    const { banner, bannerGroup, page } = qs.parse(location.search)
    const editMode: EDIT_MODE = banner ? EDIT_MODE.UPDATE : EDIT_MODE.CREATE
    const bannerPage = Number(page)
    return (
        <Save
            bannerUuid={banner as string}
            bannerGroupUuid={bannerGroup as string}
            bannerPage={bannerPage}
            editMode={editMode}
        />
    )
}

export default Wrapper
